import { queryOptions, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { HydraUIAPIError } from '@customerio/hydra-ui-api';
import type { operations } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import {
  facebookLeadAdsCredentialKey,
  facebookLeadAdsFormKeys,
  parseFacebookLeadAdsCredential,
  parseFacebookLeadAdsForm,
  type FacebookLeadAdsCredential,
  type FacebookLeadAdsForm,
} from './entity';

type FacebookAccessTokenScopesQuery = NonNullable<
  operations['get-v1-environments-environment_id-facebook_access_token_scopes']['parameters']['query']
>;

/**
 * @public The Facebook Lead Ads credential, or `null` when not connected. Only
 * a not-found response is read as "not connected"; any other failure is raised,
 * so a workspace with a page is never mistaken for one without.
 */
export async function getFacebookLeadAdsCredential(workspaceId: number): Promise<FacebookLeadAdsCredential | null> {
  try {
    const data = await hydraUiApi.facebookLeadAds.getFacebookLeadAdsCredentials({
      params: { path: { environment_id: String(workspaceId) } },
    });
    return data.facebook_lead_ads_credential == null
      ? null
      : parseFacebookLeadAdsCredential(data.facebook_lead_ads_credential);
  } catch (error) {
    if (error instanceof HydraUIAPIError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/** @public */
export const getFacebookLeadAdsCredentialQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: facebookLeadAdsCredentialKey(workspaceId),
    queryFn: () => getFacebookLeadAdsCredential(workspaceId),
    staleTime: 60_000,
  });

/** @public */
export const useFacebookLeadAdsCredential = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getFacebookLeadAdsCredentialQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getFacebookLeadAdsCredentialQueryOptions(workspaceId), ...queryConfig });
};

/** @public The workspace's Facebook Lead Ads forms (empty when none / not connected). */
export async function getFacebookLeadAdsForms(workspaceId: number, qc: QueryClient): Promise<FacebookLeadAdsForm[]> {
  const credential = await qc.fetchQuery(getFacebookLeadAdsCredentialQueryOptions(workspaceId));
  if (credential == null) {
    return [];
  }

  let data: Awaited<ReturnType<typeof hydraUiApi.facebookLeadAds.getFacebookLeadAdsForms>>;
  try {
    data = await hydraUiApi.facebookLeadAds.getFacebookLeadAdsForms({
      params: { path: { environment_id: String(workspaceId) } },
    });
  } catch (error) {
    if (error instanceof HydraUIAPIError && error.status === 403) {
      return [];
    }
    throw error;
  }
  const parsed = (data.facebook_lead_ads_forms ?? []).map(parseFacebookLeadAdsForm);
  for (const f of parsed) {
    qc.setQueryData(facebookLeadAdsFormKeys.detail(workspaceId, f.id), f);
  }
  return parsed;
}

/** @public */
export const getFacebookLeadAdsFormsQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: facebookLeadAdsFormKeys.list(workspaceId, undefined),
    queryFn: () => getFacebookLeadAdsForms(workspaceId, qc),
    staleTime: 0,
  });

/** @public */
export const useFacebookLeadAdsForms = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getFacebookLeadAdsFormsQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useQuery({ ...getFacebookLeadAdsFormsQueryOptions(workspaceId, qc), ...queryConfig });
};

/**
 * The permissions a Facebook user access token was granted.
 *
 * Facebook's login dialog lets a person untick permissions, so a token that
 * signed in may still be unable to read leads. Only the API can inspect a
 * token, because the check needs the app secret, so the token goes there and
 * the list of granted scopes comes back.
 *
 * Not cached: the token is short-lived and asked about once, right after the
 * sign-in that produced it.
 */
export async function getFacebookAccessTokenScopes(
  workspaceId: number,
  accessToken: FacebookAccessTokenScopesQuery['access_token'],
): Promise<string[]> {
  const data = await hydraUiApi.facebookLeadAds.getFacebookAccessTokenScopes({
    params: { path: { environment_id: String(workspaceId) }, query: { access_token: accessToken } },
  });
  return data.scopes;
}
